import React from 'react';

import { Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { Box } from '@mui/system';

import {
  createNote,
  createPharmacyNote,
  ToUserTypings,
  TypeOfUser,
  updateNote,
  updatePharmacyNote,
} from '@pharmaplan/common';

import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import useUser from '../../../../hooks/useUser';
import strings from '../../../../localization';
import { resetDialog } from '../../../../reducers/dialogReducer';
import CustomButton from '../../../common/CustomButton';
import CustomTextField from '../../../common/CustomTextField';

import Validator from './Validator';
import classes from './styles';

interface IAddNoteModal {
  id: string;
  update: boolean;
}

const AddNoteModal = ({ id, update }: IAddNoteModal) => {
  const dispatch = useAppDispatch();
  const { pharmacistNotesId, pharmacyNotesId } = idStore;

  const closeDialog = () => {
    dispatch(resetDialog());
  };

  const { notes, userType } = useUser();
  const professionalConfig = {
    findId: pharmacistNotesId,
    updateNote,
    updateBaseParams: { noteId: id },
    createBaseParams: { pharmacyId: id },
    createNote,
  };

  const userConfig = ToUserTypings({
    [TypeOfUser.pharmacist]: { ...professionalConfig },
    [TypeOfUser.technician]: professionalConfig,
    [TypeOfUser.student]: professionalConfig,
    [TypeOfUser.pharmacy]: {
      findId: pharmacyNotesId,
      updateNote: updatePharmacyNote,
      updateBaseParams: { pharmacistNotesId: id },
      createBaseParams: { pharmacistId: id },
      createNote: createPharmacyNote,
    },
  });

  const text = update
    ? notes.find(
      (item: { [x: string]: string }) =>
        item[userConfig[userType].findId] === id,
    )?.note
    : '';

  const handleSubmit = (values: { note: string }) => {
    dispatch(
      update
        ? userConfig[userType].updateNote({
          ...userConfig[userType].updateBaseParams,
          note: values.note,
        })
        : userConfig[userType].createNote({
          ...userConfig[userType].createBaseParams,
          note: values.note,
        }),
    );
    closeDialog();
  };

  const formik = useFormik({
    initialValues: {
      note: text ?? '',
    },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: Validator,
  });

  return (
    <Box sx={classes.container}>
      <Typography sx={classes.title}>
        {update ? strings.updateNote : strings.createNewNote}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <Typography>{strings.notes}</Typography>
        </Grid>
        <Grid item xs={10}>
          <CustomTextField
            variant="filled"
            multiline
            maxLength={200}
            id="note"
            name="note"
            formik={formik}
          />
        </Grid>
      </Grid>
      <Box sx={classes.buttonContainer}>
        <CustomButton
          customClass={classes.buttons}
          variant="outlined"
          label={strings.cancel}
          onClick={closeDialog}
        />
        <CustomButton
          customClass={classes.buttons}
          label={update ? strings.update : strings.create}
          onClick={formik.handleSubmit}
        />
      </Box>
    </Box>
  );
};

export default AddNoteModal;
